import { Box, Button } from "@vinomofo/components";
import { MouseEvent, useEffect, useRef } from "react";
import { connectAutoComplete } from "react-instantsearch-dom";
import isEmpty from "lodash.isempty";
import styled from "styled-components";
import { useSearch } from "../../hooks/use-search";
import { trackEvent } from "../../utils/track-event";
import { BasicDoc, Hit } from "react-instantsearch-core";

type QuerySuggestion = {
  objectId: string;
  query: string;
  nb_words?: number;
  popularity?: number;
} & {
  [indexName: string]: {
    exact_nb_hits: number;
  } & Partial<Hit<BasicDoc>>;
};

const Container = styled(Box)<{
  maxHeight: string;
  position: string;
  top: string;
}>`
  position: ${({ position }) => position};
  top: ${({ top }) => top};
  left: 0;
  right: 0;
  list-style: none;
  box-shadow: rgba(0, 0, 0, 0.2) 0 5px 15px 0;
  background: white;
  z-index: 100;
  max-height: ${({ maxHeight }) => maxHeight};

  li {
    margin: 0;
    border-bottom: 1px solid ${({ theme }) => theme.colors.divider};

    &:last-child {
      border-bottom: 0;
    }
  }
`;

const Suggestion = styled(Button)`
  width: 100%;
  padding: 10px;
  display: flex;
  flex-direction: row;
  align-items: center;
  transition: color 0.2s ease-out 0s, background-color 0.2s ease-out 0s;

  svg {
    width: 20px;
    height: 20px;
    margin-right: 10px;
    color: ${({ theme }) => theme.colors.divider};
  }

  &:hover {
    background-color: ${({ theme }) => theme.colors.almostWhite};

    svg {
      color: inherit;
    }
  }

  span {
    margin-left: 5px;
  }
`;

interface AutoCompleteProps {
  hits: QuerySuggestion[];
  currentRefinement: string;
  close?: () => void;
  onResult?: (result: boolean) => void;
  maxHeight?: string;
  position?: "absolute" | "relative";
  top?: string;
}

export const AutoComplete = connectAutoComplete(
  ({
    hits,
    currentRefinement,
    close,
    maxHeight = "400px",
    position = "absolute",
    top = "100%",
    onResult,
  }: AutoCompleteProps) => {
    if (isEmpty(currentRefinement) || hits.length === 0) {
      onResult && onResult(false);
      return null;
    } else {
      onResult && onResult(true);
    }
    const containerRef = useRef<HTMLUListElement>(null);
    const { updateSearch, setShowSuggestions } = useSearch();

    const getExactNbHits = (hit: QuerySuggestion | Hit<BasicDoc>): number => {
      const indexName = process.env.GATSBY_ALGOLIA_OFFERS_INDEX;
      return indexName ? hit[indexName]?.exact_nb_hits || 0 : 0;
    };

    const totalResults = (
      hit: QuerySuggestion | Hit<BasicDoc>
    ): number | string => {
      return getExactNbHits(hit) || "";
    };

    useEffect(() => {
      const handleClickOutside = (event: Event) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(event.target as Node)
        ) {
          setShowSuggestions(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [close]);

    return (
      <Container
        ref={containerRef}
        as="ul"
        backgroundColor="white"
        borderRadius="5px"
        marginTop="20px"
        overflow="scroll"
        maxHeight={maxHeight}
        position={position}
        top={top}
      >
        {hits.map((hit) => (
          <li key={hit.objectID}>
            <Suggestion
              onClick={(event: MouseEvent) => {
                event.preventDefault();
                updateSearch(hit.query);
                if (hit && hit.query) {
                  trackEvent("Query Suggestion", {
                    event_category: "Query Suggestion",
                    event_label: "Query Suggestion Clicked",
                    event_type: "click",
                    value: hit?.query,
                    query: hit?.query,
                    nbWords: hit?.nb_words,
                    popularity: hit?.popularity,
                    exactNbHits: totalResults(hit),
                    queryID: hit.__queryID,
                    position: hit.__position,
                  });
                  if (close) close();
                }
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 26 26"
                fill="currentColor"
              >
                <path d="M24.4 23L18 16.6A8.96 8.96 0 0 0 11 2c-5 0-9 4-9 9s4 9 9 9c2.1 0 4.1-.7 5.6-2l6.4 6.4 1.4-1.4zM4 11c0-3.9 3.1-7 7-7s7 3.1 7 7-3.1 7-7 7-7-3.1-7-7z"></path>
              </svg>
              {hit.query}
            </Suggestion>
          </li>
        ))}
      </Container>
    );
  }
);
